/* eslint-disable import/no-default-export */
/* eslint-disable no-console */
import {
  QueryResult,
  gql,
  isApolloError,
  useApolloClient,
} from '@apollo/client';
import ExpoCachedImage from 'expo-cached-image';
import * as Device from 'expo-device';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  Image,
  ImageResizeMode,
  StyleProp,
  ImageStyle,
} from 'react-native';

import { Box } from '@components/Restyle';
import {
  File,
  LocalFile,
  useDocumentsByClientIdsLazyQuery,
  useUserAvatarsByIdsLazyQuery,
  useChatAvatarsByIdsLazyQuery,
  useTeamAvatarsByIdsLazyQuery,
  UserAvatarsByIdsQuery,
  ChatAvatarsByIdsQuery,
  TeamAvatarsByIdsQuery,
} from '@graphql/generated';
import {
  useGetDocumentsByClientIdsQuery,
  isUrlExpired,
} from '@hooks/useGetDocumentsByClientIdsQuery';
import {
  AvatarCachePrefix,
  AvatarThumbnailCachePrefix,
} from '@src/constants/constants';
import theme from '@themes/theme';
import {
  avatarFetchedAtFromUrl,
  getAvatarUrlTimestamp,
  setAvatarUrlWithTimestamp,
  shouldRefetchAvatarUrl,
} from '@utils/avatarUrlCache';
import { AsyncStorage } from '@utils/storage';
import {
  getExpiryTimestamp,
  isUrlExpired as isSignatureExpired,
  shouldReplaceAvatarSource,
} from '@utils/urlExpiry';

// Minimal fragment used to read an avatar thumbnail straight out of the Apollo
// cache (Path A). Kept narrow (id + thumbnail only) so the read succeeds
// regardless of which query populated the File — list fragments don't all
// include every AvatarDetail field. (TA-9793)
const CACHED_AVATAR_THUMB_FRAGMENT = gql`
  fragment CachedImageAvatarThumb on File {
    id
    thumbnail
  }
`;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const isLocalUri = (url?: string | null) =>
  !!url && (url.startsWith('file:') || url.startsWith('data:'));

const inFlight = new Map<string, Promise<string>>();

const TRANSPARENT_PX =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';

const EMPTY_IMAGE = {
  id: '',
  url: '',
  thumbnail: '',
  path: '',
  cdnBaseUrl: '',
} as File & LocalFile;

// TA-10111: ExpoCachedImage is not wrapped in React.memo. Every re-render of
// CachedImage (from any internal hook change) causes ExpoCachedImage to
// re-render and pass a new `{ ...source, uri: imgUri }` object to RN's Image —
// even when the URI is identical — triggering a brief native clear/reload flash.
// Custom comparator stops that cycle: only re-render when the resolved image URI
// or cache key actually changes, while still propagating real changes (new image,
// dimension change, placeholder swap).
const StableExpoCachedImage = React.memo(
  ExpoCachedImage as React.ComponentType<any>,
  (prev: any, next: any) => {
    // Compare by the stable S3/CDN path (before `?`) so that presigned URL
    // rotation — same object, different X-Amz-Date/Signature in the query
    // string — does not count as a new image. Full-string comparison would
    // let AsyncStorage-cached URLs (from a prior session) and Apollo-cache
    // URLs (from the current session) diverge and trigger a re-render even
    // though both point to the same bytes. (TA-10111)
    const prevPath = prev.source?.uri?.split('?')[0];
    const nextPath = next.source?.uri?.split('?')[0];
    const uriEqual = prevPath === nextPath;
    const keyEqual = prev.cacheKey === next.cacheKey;
    const styleEqual = prev.style === next.style;
    const resizeEqual = prev.resizeMode === next.resizeMode;
    const loadEqual = prev.onLoad === next.onLoad;
    const phEqual = prev.placeholderContent === next.placeholderContent;
    const isEqual =
      uriEqual && keyEqual && styleEqual && resizeEqual && loadEqual && phEqual;
    return isEqual;
  }
);

// TA-10914: avatar sources are only ever swapped (see `acceptedAvatarUrl`
// below) once the previously-held signature has actually expired or the
// underlying object changed — the value itself is already stable across
// valid same-object signature rotations. So, unlike StableExpoCachedImage
// above (whose callers still pass a brand-new URI object on every valid
// rotation and rely on path-stripping to suppress the reload), avatars can
// safely compare the full URI: a change here always means a real update is
// warranted, and a path-stripped comparison would wrongly swallow the
// expired -> renewed swap this ticket fixes.
//
// Exported as a standalone, named function (rather than inlined in
// React.memo) so it can be unit-tested directly — CachedImage's own
// `avatarUrl` state only ever transitions once per mount (see the fetch
// effect below), so there's no way to exercise a live "held X, comparator
// must let Y through" transition purely through the component's rendered
// output; testing the comparator's own equality logic is the only way to
// verify it without exporting internals.
type AvatarExpoCachedImageProps = {
  source?: { uri?: string };
  cacheKey?: string;
  style?: StyleProp<ImageStyle>;
  resizeMode?: ImageResizeMode;
  onLoad?: () => void;
  placeholderContent?: React.ReactElement;
};

export const avatarExpoCachedImagePropsEqual = (
  prev: AvatarExpoCachedImageProps,
  next: AvatarExpoCachedImageProps
): boolean => {
  const uriEqual = prev.source?.uri === next.source?.uri;
  const keyEqual = prev.cacheKey === next.cacheKey;
  const styleEqual = prev.style === next.style;
  const resizeEqual = prev.resizeMode === next.resizeMode;
  const loadEqual = prev.onLoad === next.onLoad;
  const phEqual = prev.placeholderContent === next.placeholderContent;
  return (
    uriEqual && keyEqual && styleEqual && resizeEqual && loadEqual && phEqual
  );
};

const StableAvatarExpoCachedImage = React.memo(
  ExpoCachedImage as React.ComponentType<AvatarExpoCachedImageProps>,
  avatarExpoCachedImagePropsEqual
);

type CachedImagePropsType = {
  avatarId?: string;
  avatarType?: 'chat' | 'team' | 'user';
  imageClientId?: string;
  image?: File | LocalFile | null;
  width?: number;
  screenWidth?: number;
  height?: number;
  isRemoveable?: boolean;
  croppingStrategy?: 'c-at_max_enlarge';
  resizeMode?: ImageResizeMode;
  borderRadius?: number;
  skipCache?: boolean;
  style?: Omit<StyleProp<ImageStyle>, 'width' | 'height' | 'opacity'>;
  placeholderContentFlex?: 0 | 1;
  from?: string;
  contentType?: string | null;
  // Overrides the default grey02 box, shown until the image has actually
  // finished loading — not just until its URL resolves (TA-10278). Omitted
  // callers keep the existing plain-grey placeholder.
  placeholderContent?: React.ReactElement;
  testID?: string;
  // Full-screen viewers (TA-11066): until the clientId lookup resolves an S3
  // URL — or when it can't, e.g. "Documents not found" for a project viewer —
  // show the doc's own file.url, else the untransformed CDN path, instead of
  // a resized/recompressed ImageKit rendition that blurs once zoomed.
  fullResolution?: boolean;
};

const AVATAR_REFRESH_MIN_INTERVAL_MS = 60_000;

const CachedImage: React.FC<CachedImagePropsType> = ({
  avatarId,
  avatarType = 'user',
  imageClientId,
  image,
  width,
  screenWidth,
  height,
  isRemoveable,
  borderRadius,
  resizeMode,
  croppingStrategy,
  skipCache = true,
  style: styleProp,
  placeholderContentFlex = 1,
  from = '',
  contentType,
  placeholderContent,
  testID,
  fullResolution = false,
}) => {
  const safeImage = image ?? EMPTY_IMAGE;
  // GraphQL hooks
  const apolloClient = useApolloClient();
  const [userAvatarsByIdsThumbnailQurey] = useUserAvatarsByIdsLazyQuery();
  const [chatAvatarsByIdsThumbnailQurey] = useChatAvatarsByIdsLazyQuery();
  const [teamAvatarsByIdsThumbnailQurey] = useTeamAvatarsByIdsLazyQuery();
  const [documentsByClientIdsQurey] = useDocumentsByClientIdsLazyQuery();
  const { batchIndividualRequest, isRequestInFlight } =
    useGetDocumentsByClientIdsQuery();

  const thumbnailWidth = 300;
  const getCachedImageForPlatform = () => {
    if (Platform.OS === 'web' || skipCache) return Image;
    return avatarId ? StableAvatarExpoCachedImage : StableExpoCachedImage;
  };
  const CachedImageForPlatform = getCachedImageForPlatform();

  const [isLocal, setIsLocal] = useState(() => isLocalUri(safeImage.url));
  useEffect(() => {
    const current = isLocalUri(safeImage.url);
    if (current != isLocal) {
      setIsLocal(current);
    }
  }, [safeImage.url]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const safeSet =
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (v: T) => {
      if (mountedRef.current) {
        setter((prev) => (Object.is(prev, v) ? prev : v));
      }
    };

  const batchRef = useRef(batchIndividualRequest);
  const inflightRef = useRef(isRequestInFlight);
  const docsQueryRef = useRef(documentsByClientIdsQurey);
  useEffect(() => {
    batchRef.current = batchIndividualRequest;
  }, [batchIndividualRequest]);
  useEffect(() => {
    inflightRef.current = isRequestInFlight;
  }, [isRequestInFlight]);
  useEffect(() => {
    docsQueryRef.current = documentsByClientIdsQurey;
  }, [documentsByClientIdsQurey]);

  const [imageUrl, setImageUrl] = useState('');
  const setImageUrlSafe = safeSet(setImageUrl);

  useEffect(() => {
    if (!imageClientId || isLocal) return;
    // do not use cache url when can display thumbnail
    if ((width ?? thumbnailWidth) < thumbnailWidth && safeImage.thumbnail) {
      setImageUrlSafe(safeImage.thumbnail);
      return;
    }

    const ac = new AbortController();

    (async () => {
      const cached = await AsyncStorage.getItem(imageClientId);
      if (cached) {
        if (!isUrlExpired(cached, new Date())) {
          setImageUrlSafe(cached);
          return;
        }
        await AsyncStorage.deleteItem(imageClientId);
      }
      if (inflightRef.current(imageClientId)) {
        const started = Date.now();
        while (
          Date.now() - started < 1000 &&
          inflightRef.current(imageClientId)
        ) {
          await sleep(200);
          const probe = await AsyncStorage.getItem(imageClientId);
          if (probe && !isUrlExpired(probe, new Date())) {
            setImageUrlSafe(probe);
            return;
          }
        }
      }

      try {
        batchRef.current?.(imageClientId);
      } catch (e) {
        console.log('[img-byClientId] batch fire-and-forget error →', e);
      }

      const started = Date.now();
      for (let i = 0; i < 20; i++) {
        const probe = await AsyncStorage.getItem(imageClientId);
        if (probe) {
          setImageUrlSafe(probe);
          return;
        }
        if (Date.now() - started > 4000) break;
        await sleep(200);
      }

      const key = `doc:${imageClientId}`;
      let p = inFlight.get(key);
      if (!p) {
        p = (async () => {
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              const rtn: any = await docsQueryRef.current({
                variables: { clientIds: [imageClientId] },
                context: { fetchOptions: { signal: ac.signal } },
              });
              const url = rtn.data?.documentsByClientIds?.[0]?.file?.url ?? '';
              if (!url) {
                console.warn('File URL not found');
                return '';
              }
              await AsyncStorage.setItem(imageClientId, url);
              return url;
            } catch (err: any) {
              if (err?.name === 'AbortError') throw err;
              const isDocumentsNotFoundError =
                isApolloError(err as Error) &&
                err.graphQLErrors?.some(
                  ({ message, path }: any) =>
                    message?.includes('Documents not found') &&
                    path?.includes('documentsByClientIds')
                ) === true;
              if (isDocumentsNotFoundError) {
                return '';
              }
              if (attempt < 2) {
                await sleep(500 * (attempt + 1));
                continue;
              }
              throw err;
            }
          }
          return '';
        })();
        inFlight.set(key, p);
        p.finally(() => {
          if (inFlight.get(key) === p) inFlight.delete(key);
        });
      }

      try {
        const fresh = await p;
        if (fresh) setImageUrlSafe(fresh);
      } catch (e) {
        console.log('Error fetching image:', e);
      }
    })();

    return () => {
      ac.abort();
    };
  }, [imageClientId, isLocal]);

  const [avatarUrl, setAvatarUrl] = useState('');
  const setAvatarUrlSafe = safeSet(setAvatarUrl);
  // TA-10915: requested (rate-limited) when the avatar being shown has expired
  // and nothing fresher arrived on its own. The fetch effect below only ran
  // once per mount, so a long-lived avatar (e.g. the web drawer's My Account
  // icon, which never unmounts) otherwise stayed on its expired signature —
  // grey — until something remounted it.
  //
  // The request is tied to the avatarId/width it was made for, so it forces a
  // cache-bypassing fetch for exactly that one re-run: a later change of
  // avatar (e.g. the user uploads a new one) goes back to the normal caches.
  const [avatarRefresh, setAvatarRefresh] = useState({
    nonce: 0,
    avatarId,
    width,
  });
  // Last time a network fetch for the current avatar started (initial or
  // refresh), and the id of the fetch run still in flight (0 = none). Every
  // run owns a token so a stale run's `finally` cannot clear a newer run's flag.
  const consumedAvatarRefreshRef = useRef(0);
  const lastAvatarFetchRef = useRef({ avatarId, at: 0 });
  const avatarFetchRunRef = useRef(0);
  const avatarFetchInFlightRef = useRef(0);

  useEffect(() => {
    if (!avatarId || isLocal || imageClientId) return;

    const ac = new AbortController();

    const cachePrefix =
      (width ?? 100) <= 75 ? AvatarThumbnailCachePrefix : AvatarCachePrefix;
    const aCacheKey = `${cachePrefix}${avatarId}`;

    const pickQuery = () => {
      switch (avatarType) {
        case 'chat':
          return chatAvatarsByIdsThumbnailQurey;
        case 'team':
          return teamAvatarsByIdsThumbnailQurey;
        case 'user':
        default:
          return userAvatarsByIdsThumbnailQurey;
      }
    };

    const extract = (
      type: 'user' | 'chat' | 'team',
      rtn:
        | QueryResult<UserAvatarsByIdsQuery>
        | QueryResult<ChatAvatarsByIdsQuery>
        | QueryResult<TeamAvatarsByIdsQuery>
    ) => {
      switch (type) {
        case 'user':
          return (
            (rtn as QueryResult<UserAvatarsByIdsQuery>).data
              ?.userAvatarsByIds?.[0]?.thumbnail ?? ''
          );
        case 'chat':
          return (
            (rtn as QueryResult<ChatAvatarsByIdsQuery>).data
              ?.chatAvatarsByIds?.[0]?.thumbnail ?? ''
          );
        case 'team':
          return (
            (rtn as QueryResult<TeamAvatarsByIdsQuery>).data
              ?.teamAvatarsByIds?.[0]?.thumbnail ?? ''
          );
      }
    };

    const runId = ++avatarFetchRunRef.current;
    avatarFetchInFlightRef.current = runId;
    // A refresh request means the URL we hold is known-expired: skip the local
    // and Apollo caches, which would hand the same signature back.
    const forceRefresh =
      avatarRefresh.nonce > consumedAvatarRefreshRef.current &&
      avatarRefresh.avatarId === avatarId &&
      avatarRefresh.width === width;
    // Each request is honoured once: returning to the same avatarId/width later
    // (a1 -> a2 -> a1) must not re-trigger a cache bypass.
    if (forceRefresh) consumedAvatarRefreshRef.current = avatarRefresh.nonce;
    (async () => {
      const nowMs = Date.now();
      const cached = forceRefresh
        ? null
        : await AsyncStorage.getItem(aCacheKey);
      if (cached) {
        const cachedTs = await getAvatarUrlTimestamp(aCacheKey);
        // Only display the cached URL if it is still valid. Showing it first
        // and validating afterwards could briefly render an expired presigned
        // URL (403 / broken image) before the refetch returns. (TA-9793)
        if (!shouldRefetchAvatarUrl(cached, cachedTs, nowMs)) {
          setAvatarUrlSafe(cached);
          return;
        }
      }

      // Path A — the avatar thumbnail is usually already in the Apollo cache
      // from the list/chat query that rendered this avatar. Read it directly
      // and skip the network round-trip; only fall back to the lazy query on a
      // cold cache (first install or cache cleared). (TA-9793)
      try {
        const fragData = forceRefresh
          ? null
          : apolloClient.readFragment<{
              id: string;
              thumbnail?: string | null;
            }>({
              id: apolloClient.cache.identify({
                __typename: 'File',
                id: avatarId,
              }),
              fragment: CACHED_AVATAR_THUMB_FRAGMENT,
            });
        const cachedThumb = fragData?.thumbnail;
        // Use the URL's own minted time (not `now`) so we never extend a
        // near-expiry presigned URL by a full TTL window, while CDN thumbnails
        // (no S3 expiry params) are still treated as fresh. A genuinely
        // expired presigned URL falls through to the cold-cache lazy query
        // instead of being displayed/re-cached as broken. (TA-9793)
        const thumbFetchedAt = cachedThumb
          ? avatarFetchedAtFromUrl(cachedThumb, nowMs)
          : nowMs;
        if (
          cachedThumb &&
          !shouldRefetchAvatarUrl(cachedThumb, thumbFetchedAt, nowMs)
        ) {
          setAvatarUrlSafe(cachedThumb);
          await setAvatarUrlWithTimestamp(
            aCacheKey,
            cachedThumb,
            thumbFetchedAt
          );
          return;
        }
      } catch {
        // fall through to the cold-cache lazy query below
      }

      lastAvatarFetchRef.current = { avatarId, at: Date.now() };
      const key = `avatar:${aCacheKey}${forceRefresh ? ':refresh' : ''}`;
      let p = inFlight.get(key);
      if (!p) {
        p = (async () => {
          const query = pickQuery();
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              // TA-10914: we only reach this cold-cache path once the checks
              // above have already decided the held URL needs refreshing.
              // Apollo's default cache-first policy would otherwise hand
              // back this exact same (stale) result for the identical
              // {avatarId} variables instead of hitting the network, and the
              // write below would then re-persist that stale signature with
              // a fresh `Date.now()` timestamp — extending trust in a URL
              // that never actually got renewed. network-only guarantees the
              // result (and therefore the persisted timestamp) is genuinely
              // fresh.
              const rtn: any = await query({
                variables: { ids: [avatarId] },
                fetchPolicy: 'network-only',
                context: { fetchOptions: { signal: ac.signal } },
              });
              const url = extract(avatarType, rtn) || '';
              if (!url) {
                console.warn('[avatar] thumbnail not found in response');
                return '';
              }
              await setAvatarUrlWithTimestamp(aCacheKey, url, Date.now());
              return url;
            } catch (err: any) {
              if (err?.name === 'AbortError') throw err;
              if (attempt < 2) {
                await sleep(500 * (attempt + 1));
                continue;
              }
              throw err;
            }
          }
          return '';
        })();
        inFlight.set(key, p);
        // The derived promise from finally() re-rejects when the fetch fails;
        // the awaiting caller below handles the error, so swallow it here or
        // every failed refresh surfaces as an unhandled rejection.
        p.finally(() => {
          if (inFlight.get(key) === p) inFlight.delete(key);
        }).catch(() => {});
      }

      try {
        const fresh = await p;
        if (fresh) setAvatarUrlSafe(fresh);
      } catch (e) {
        console.log('Error fetching avatar:', e);
      }
    })().finally(() => {
      if (avatarFetchInFlightRef.current === runId) {
        avatarFetchInFlightRef.current = 0;
      }
    });

    return () => {
      ac.abort();
    };
  }, [avatarId, imageClientId, width, isLocal, avatarRefresh]);

  const transformationList = useMemo(() => {
    const format = 'f-auto';
    const density = Device.isDevice ? 'dpr-2' : 'dpr-1';
    let quality;

    if (from === 'search') {
      quality = Device.isDevice ? 'q-40' : 'q-60';
    } else {
      quality = Device.isDevice ? 'q-60' : 'q-80';
    }
    const arr = ['/tr:', format, density, quality];
    if (width) arr.push(`w-${width}`);
    if (height) arr.push(`h-${height}`);
    if (croppingStrategy) arr.push(croppingStrategy);
    return arr.join(',');
  }, [from, width, height, croppingStrategy]);

  const bgColor = useMemo(() => {
    if (!contentType?.includes('video') && Platform.OS !== 'web')
      return theme.colors.grey02;
    if (contentType?.includes('video')) return theme.colors.black;
    if (Platform.OS === 'web') return theme.colors.grey02;
    return theme.colors.white;
  }, [contentType]);

  const style: StyleProp<ImageStyle> = useMemo(
    () => [
      {
        width: screenWidth ?? width,
        height: height || '100%',
        opacity: isLocal && !isRemoveable ? 0.5 : 1,
        backgroundColor: bgColor,
        borderRadius: borderRadius || undefined,
        ...styleProp,
      },
    ],
    [
      screenWidth,
      width,
      height,
      isLocal,
      isRemoveable,
      bgColor,
      borderRadius,
      styleProp,
    ]
  );

  const defaultPlaceholder = useMemo(
    () => (
      <Box
        backgroundColor='grey02'
        style={{
          flex: placeholderContentFlex,
          height,
          width,
          borderRadius: borderRadius || undefined,
        }}
      />
    ),
    [placeholderContentFlex, height, width, borderRadius]
  );

  // Only relevant when a caller passes a custom `placeholderContent`
  // (TA-10278) — `CachedImageForPlatform` resolves to the plain RN `Image`
  // on web regardless of `skipCache`, and on native whenever `skipCache`
  // is left at its default `true` (a few native callers explicitly pass
  // `skipCache={false}` to opt into `ExpoCachedImage` instead, which DOES
  // render `placeholderContent`). For every other caller, `Image` doesn't
  // render `placeholderContent` at all, so the default placeholder above
  // was already inert there. Rather than change that long-standing behavior
  // for every avatar/image consumer, opt-in callers get a real overlay
  // instead, shown until the image has actually finished loading — not
  // just until its URL resolves.
  const [imageLoaded, setImageLoaded] = useState(false);
  useEffect(() => {
    setImageLoaded(false);
  }, [imageUrl, avatarUrl]);
  // Stable callback so StableExpoCachedImage's comparator sees the same
  // function reference across re-renders and doesn't break memoization.
  const handleImageLoad = useCallback(() => setImageLoaded(true), []);

  // TA-10111: Synchronous Apollo cache read gives the same canonical URL that
  // Path A's async effect will resolve to. Both paths now start from the same
  // stable presigned URL so source.uri never transitions on subscription
  // events, and StableExpoCachedImage never re-renders for those non-changes.
  const canonicalAvatarThumb = useMemo(() => {
    if (!avatarId || imageClientId) return null;
    try {
      const fragData = apolloClient.readFragment<{
        id: string;
        thumbnail?: string | null;
      }>({
        id: apolloClient.cache.identify({ __typename: 'File', id: avatarId }),
        fragment: CACHED_AVATAR_THUMB_FRAGMENT,
      });
      return fragData?.thumbnail?.trim() || null;
    } catch {
      return null;
    }
  }, [avatarId, imageClientId, apolloClient]);

  let cdnUrl = '';
  let cacheKey = '';

  if (imageClientId) {
    const cdnTransformation = fullResolution ? '' : transformationList;
    const cdnPathUrl = safeImage.path
      ? `${safeImage.cdnBaseUrl}${cdnTransformation}${safeImage.path}`
      : '';
    const cdnUrlBig =
      imageUrl || (fullResolution && safeImage.url) || cdnPathUrl;
    cacheKey = imageClientId;
    cdnUrl =
      (width ?? thumbnailWidth) < thumbnailWidth
        ? (safeImage.thumbnail ?? cdnUrlBig)
        : cdnUrlBig;
  } else {
    cdnUrl = safeImage.path
      ? `${safeImage.cdnBaseUrl}${transformationList}${safeImage.path}`
      : (safeImage.thumbnail ?? '');
    cacheKey = safeImage.path
      ? `${transformationList}${safeImage.path}`.replaceAll(/\W+/gi, '_')
      : '';
  }

  const avatarFallback =
    avatarId && !imageClientId
      ? canonicalAvatarThumb || safeImage.thumbnail || cdnUrl
      : cdnUrl;
  const finalUrl = avatarId && avatarUrl ? avatarUrl : avatarFallback;
  const safeUrl =
    finalUrl && finalUrl.trim() !== '' ? finalUrl : TRANSPARENT_PX;
  const isRealUrl = safeUrl !== TRANSPARENT_PX && !safeUrl.startsWith('data:');
  // S3 presigned URLs rotate their query-string signature on every server
  // response; only the path (before `?`) identifies the same object. Using the
  // path as the memo key prevents `Image` from seeing a new `source` object —
  // and therefore re-loading the image — when only the signing params change.
  const safeUrlPath = isRealUrl ? safeUrl.split('?')[0] : safeUrl;

  // TA-10914: for avatars only, hold onto the last-accepted full URL (path +
  // signature) across renders — the "store info from previous render during
  // render" pattern — and only swap in a newly-arrived candidate when it
  // points at a different object, or the held signature has actually
  // expired/is invalid. A same-object rotation while the held signature is
  // still valid is ignored entirely (preserves the no-flicker guarantee from
  // TA-10111); an expired held signature is always replaced, so a stuck grey
  // avatar recovers as soon as a fresh URL arrives, instead of the old
  // path-only memo silently keeping the expired one forever.
  //
  // Re-run this on every render, not just when `safeUrl` changes since the
  // last one — `shouldReplaceAvatarSource` is already a no-op once
  // `acceptedAvatarUrl` matches `safeUrl`, so gating it behind a "did the
  // candidate change" check bought nothing but a bug: a previously-rejected
  // (valid-at-the-time) rotation would never be reconsidered once
  // `acceptedAvatarUrl` later expired and `safeUrl` stopped changing.
  const [acceptedAvatarUrl, setAcceptedAvatarUrl] = useState(safeUrl);
  const acceptedAvatarIsRealUrl =
    acceptedAvatarUrl !== TRANSPARENT_PX &&
    !acceptedAvatarUrl.startsWith('data:');
  if (
    avatarId &&
    shouldReplaceAvatarSource(
      acceptedAvatarIsRealUrl ? acceptedAvatarUrl : null,
      isRealUrl ? safeUrl : null,
      new Date()
    )
  ) {
    setAcceptedAvatarUrl(safeUrl);
  }

  // PR review (TA-10914): the promotion check above only re-runs when this
  // component actually renders. If a same-object rotation is rejected while
  // still valid and no fresh candidate arrives afterward, nothing forces a
  // later render once the held signature actually expires — the outer
  // `React.memo(CachedImage)` can keep bailing on unrelated parent
  // re-renders forever, leaving a stale/403 avatar displayed indefinitely.
  // Schedule a promotion check for exactly the held URL's real expiry
  // moment instead of relying on an incidental re-render to catch it.
  useEffect(() => {
    if (!avatarId) return undefined;
    const expiresAt = acceptedAvatarIsRealUrl
      ? getExpiryTimestamp(acceptedAvatarUrl)
      : null;
    if (expiresAt == null) return undefined;

    // +1s margin: a timer that fires at the exact expiry millisecond would
    // have `isUrlExpired`'s strict `expirationDate < now` still read as
    // "not yet expired" on that boundary tick. Firing a moment after
    // guarantees the promotion check below actually sees it as expired.
    let timer: ReturnType<typeof setTimeout>;
    const attempt = () => {
      const now = new Date();
      const candidate = isRealUrl ? safeUrl : null;
      if (shouldReplaceAvatarSource(acceptedAvatarUrl, candidate, now)) {
        setAcceptedAvatarUrl(safeUrl);
        return;
      }
      if (!isSignatureExpired(acceptedAvatarUrl, now, 0)) return;
      // The held signature has expired and no fresher candidate is available:
      // ask the server for a new one. Refreshes are spaced at least a cooldown
      // apart so a server that keeps returning already-expired URLs cannot
      // loop, and a refresh that failed or came back expired is retried after
      // the cooldown (this timer is one-shot and none of this effect's deps
      // change in that case). A refresh still in flight is never restarted.
      const last = lastAvatarFetchRef.current;
      const cooldownLeft =
        last.avatarId === avatarId
          ? last.at + AVATAR_REFRESH_MIN_INTERVAL_MS - now.getTime()
          : 0;
      if (avatarFetchInFlightRef.current !== 0 || cooldownLeft > 0) {
        timer = setTimeout(attempt, Math.max(cooldownLeft, 0) + 1000);
        return;
      }
      lastAvatarFetchRef.current = { avatarId, at: now.getTime() };
      setAvatarRefresh((r) => ({ nonce: r.nonce + 1, avatarId, width }));
      timer = setTimeout(attempt, AVATAR_REFRESH_MIN_INTERVAL_MS + 1000);
    };
    timer = setTimeout(attempt, Math.max(expiresAt - Date.now(), 0) + 1000);
    return () => clearTimeout(timer);
  }, [
    avatarId,
    width,
    acceptedAvatarUrl,
    acceptedAvatarIsRealUrl,
    safeUrl,
    isRealUrl,
  ]);

  // Non-avatar path is untouched: same memo, same deps (path-only) as
  // before, so imageClientId/attachment/file-preview caching and refresh
  // behavior does not change (out of scope for TA-10914).
  const generalSource = useMemo(() => {
    return isRealUrl
      ? { uri: safeUrl, expiresIn: undefined }
      : { uri: safeUrl };
  }, [isRealUrl, safeUrlPath]);

  // Avatar path: keyed on the full accepted URL, since `acceptedAvatarUrl`
  // above already guarantees it only changes when a real update is
  // warranted — no need (and no correctness benefit) to strip the query
  // string here.
  const avatarSource = useMemo(() => {
    return acceptedAvatarIsRealUrl
      ? { uri: acceptedAvatarUrl, expiresIn: undefined }
      : { uri: acceptedAvatarUrl };
  }, [acceptedAvatarIsRealUrl, acceptedAvatarUrl]);

  const source = avatarId ? avatarSource : generalSource;

  const cacheKeyForThisRender = isRealUrl ? cacheKey : `${cacheKey}:ph`;

  // TA-10939: expo-cached-image's own load/refresh logic runs inside a
  // mount-only `useEffect` (deps: []) and renders from its own internal
  // `imgUri` state — once mounted, it never notices a changed `source.uri`
  // prop (see node_modules/expo-cached-image/lib/index.js). `acceptedAvatarUrl`
  // above already only changes when a real update is warranted, so keying the
  // avatar element on it forces exactly the remount expo-cached-image needs to
  // pick up a renewed signature — without reintroducing the TA-10111 flicker,
  // since a same-object signature rotation doesn't change `acceptedAvatarUrl`
  // and therefore doesn't change this key either.
  const avatarRemountKey = avatarId ? acceptedAvatarUrl : undefined;

  // Must come after every hook above — an early return here based on
  // `isLocal` used to happen before the `source` useMemo, so switching
  // between a remote File and a local file:// image changed the hook count
  // between renders and crashed with "Rendered fewer hooks than expected"
  // (TA-10442).
  if (isLocal) {
    return (
      <Image
        testID={testID}
        source={{ uri: safeImage.url! }}
        style={style}
        crossOrigin='anonymous'
        resizeMode={resizeMode || 'cover'}
      />
    );
  }

  return (
    <Box overflow='hidden'>
      <CachedImageForPlatform
        key={avatarRemountKey}
        testID={testID}
        source={source}
        cacheKey={cacheKeyForThisRender}
        style={style}
        resizeMode={resizeMode || 'cover'}
        placeholderContent={placeholderContent ?? defaultPlaceholder}
        onLoad={placeholderContent ? handleImageLoad : undefined}
      />
      {placeholderContent && (!isRealUrl || !imageLoaded) && (
        <Box
          pointerEvents='none'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}>
          {placeholderContent}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(CachedImage);
